/**
 * Tool registration gate: wraps McpServer.tool() with preset filtering,
 * description overrides, verbosity control, savings tracking, dedup, and journal.
 */
import type { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import type { TraceMcpConfig } from '../config.js';
import type { SessionJournal } from '../session/journal.js';
import type { SessionTracker } from '../session/tracker.js';
import type { JournalEntryCallbackData } from './journal-broadcast.js';
import {
  applySchemaTransforms,
  createGatedCallback,
  type GatedCallbackContext,
  injectAnnotations,
  responseTokens,
  schemaIndexOf,
  type SchemaTransformConfig,
  stampAlwaysLoad,
  type WrappedToolResponse,
} from './tool-gate-helpers.js';
import { getToolStateScope, type ToolStateScope } from './tool-annotations.js';
import { createToolFilter } from './tool-filter.js';
import type { ToolResponse } from './types.js';
import type { WorktreeDelta } from '../worktree-delta.js';
import type { WorktreeIndexRoute } from './worktree-index-route.js';

/**
 * A tool registered but held back from this session's surface (TRA-402).
 * `enabled: false` keeps it out of `tools/list` and uncallable via
 * `tools/call`; `load_tools` flips it on and installs `handler` into the live
 * `toolHandlers` map so `batch` can reach it too.
 */
export interface DeferredTool {
  registered: { enabled: boolean; description?: string; inputSchema?: unknown };
  handler: (params: Record<string, unknown>) => Promise<ToolResponse>;
}

interface ToolGateResult {
  _originalTool: McpServer['tool'];
  registeredToolNames: string[];
  /**
   * Meta-tools registered through `_originalTool`, outside the preset gate.
   * They are advertised like any other tool, so the surface a client sees in
   * `tools/list` is these plus `registeredToolNames` — which is the basis
   * preset-surface-budget.test.ts measures, and the number the usage ping
   * reports as `tools_advertised` (TRA-643).
   */
  ungatedToolNames: string[];
  toolHandlers: Map<string, (params: Record<string, unknown>) => Promise<ToolResponse>>;
  /** Tools outside the active preset, registered-but-disabled and loadable. */
  deferredTools: Map<string, DeferredTool>;
  /**
   * Whether a registered tool keeps answering from this session's server in
   * a worktree session with a branch index — its `ToolStateScope`, recorded
   * when it was registered. Unknown names count as session-local.
   */
  isSessionLocalTool: (name: string) => boolean;
}

/**
 * Monkey-patches `server.tool` to add:
 * - Preset-based filtering (only register allowed tools)
 * - Description overrides (flat string or per-param)
 * - Verbosity control (full/minimal/none)
 * - Callback wrapping for savings tracking + journal + dedup + optimization hints
 *
 * The heavy lifting lives in ./tool-gate-helpers.ts; this function is a thin
 * composition layer that wires per-call-invariant config into those helpers.
 */
export function installToolGate(
  server: McpServer,
  config: TraceMcpConfig,
  activePreset: Set<string> | 'all',
  savings: SessionTracker,
  journal: SessionJournal,
  j: (value: unknown) => string,
  extractResultCount: (response: {
    content: Array<{ type: string; text: string }>;
    isError?: boolean;
  }) => number,
  extractCompactResult: (
    toolName: string,
    response: { content: Array<{ type: string; text: string }>; isError?: boolean },
  ) => Record<string, unknown> | undefined,
  stripMetaFields: (obj: Record<string, unknown>) => void,
  projectRoot?: string,
  recordToolCall?: (success: boolean) => void,
  onJournalEntry?: (data: JournalEntryCallbackData) => void,
  sessionId?: string,
  getWorktreeDelta?: () => Promise<WorktreeDelta | null>,
  worktreeIndex?: WorktreeIndexRoute,
): ToolGateResult {
  const descriptionOverrides = config.tools?.descriptions ?? {};
  const schemaTransformConfig: SchemaTransformConfig = {
    descriptionVerbosity: config.tools?.description_verbosity ?? 'full',
    compactSchemas: config.tools?.compact_schemas ?? false,
    descriptionOverrides,
    sharedParamOverrides:
      typeof descriptionOverrides._shared === 'object' && descriptionOverrides._shared !== null
        ? (descriptionOverrides._shared as Record<string, string>)
        : {},
  };

  // Shared with the daemon proxy's per-session filter (TRA-250) so the local
  // and daemon-backed surfaces cannot drift apart.
  const toolAllowed = createToolFilter(config, activePreset);

  const _originalTool = server.tool.bind(server);
  const registeredToolNames: string[] = [];
  const ungatedToolNames: string[] = [];
  const toolHandlers = new Map<
    string,
    (params: Record<string, unknown>) => Promise<ToolResponse>
  >();
  const deferredTools = new Map<string, DeferredTool>();
  // Worktree scope per registered tool (tool-annotations.ts, read at
  // registration like the MCP annotations).
  const toolScopes = new Map<string, ToolStateScope>();
  const isSessionLocalTool = (name: string): boolean => toolScopes.get(name) !== 'index';

  /** Build the per-call context threaded into the wrapped callback. */
  const gatedCallbackContext = (name: string): GatedCallbackContext => ({
    name,
    config,
    savings,
    journal,
    j,
    extractResultCount,
    extractCompactResult,
    stripMetaFields,
    projectRoot,
    recordToolCall,
    onJournalEntry,
    sessionId,
    getWorktreeDelta,
    worktreeIndex,
    isSessionLocalTool,
  });

  server.tool = ((...args: unknown[]) => {
    const name = args[0] as string;
    // TRA-402: a tool outside the preset is still registered — just disabled,
    // so it stays out of `tools/list` (no schema tokens) while `load_tools`
    // can turn it on mid-session. `tools.exclude` is checked separately by
    // `load_tools` so a hard exclusion can never be escalated back in.
    const allowed = toolAllowed(name);
    if (allowed) registeredToolNames.push(name);
    toolScopes.set(name, getToolStateScope(name));

    // Transform description + input schema (overrides, verbosity, compaction).
    applySchemaTransforms(args, schemaTransformConfig);

    // Wrap callback for savings/journal/dedup/hints.
    const cbIdx = args.length - 1;
    const originalCb = args[cbIdx] as (...args: unknown[]) => unknown;
    let deferredHandler: DeferredTool['handler'] | undefined;
    if (typeof originalCb === 'function') {
      const handler = async (params: Record<string, unknown>) =>
        (await originalCb(params)) as ToolResponse;
      if (allowed) toolHandlers.set(name, handler);
      else deferredHandler = handler;
      const schema = args[schemaIndexOf(args)];
      const supportsDetailLevel = !!(
        schema &&
        typeof schema === 'object' &&
        'detail_level' in schema
      );
      args[cbIdx] = createGatedCallback(
        { ...gatedCallbackContext(name), supportsDetailLevel },
        originalCb,
      );
    }

    // Inject ToolAnnotations before the callback so the MCP SDK registers
    // behavioural hints (readOnlyHint, destructiveHint, etc.).
    injectAnnotations(args);

    const registered = (_originalTool as (...args: unknown[]) => unknown)(...args);
    stampAlwaysLoad(name, registered);
    if (!allowed && registered && typeof registered === 'object') {
      // Assign the flag rather than calling `.disable()`: the SDK's disable()
      // routes through update(), which fires a tools/list_changed per call —
      // ~140 notifications on a `minimal` session. One notification is emitted
      // by load_tools instead, when the surface actually changes.
      const entry = registered as { enabled: boolean; description?: string; inputSchema?: unknown };
      entry.enabled = false;
      if (deferredHandler) deferredTools.set(name, { registered: entry, handler: deferredHandler });
    }
    return registered as ReturnType<typeof server.tool>;
  }) as typeof server.tool;

  /**
   * Count meta-tool calls, which the gate never saw (TRA-1162).
   *
   * `annotatedOriginalTool` injected annotations and the always-load stamp but
   * left the callback alone, so `savings.recordCall` — which only ever runs
   * inside `createGatedCallback` — never fired for any of UNGATED_META_TOOLS.
   * On a store of 27 959 calls across 2 604 sessions, all ten read exactly
   * zero. That is not "nobody calls them": `load_tools` is the escalation hatch
   * that makes `minimal` defensible as the shipped default — the whole argument
   * for a small default surface is that anything outside it is one round-trip
   * away — and we had no way to see whether sessions were taking that trip once
   * a day or forty times an hour.
   *
   * Deliberately not the full gate: budget clamping, dedup and wire-format
   * re-encoding have no business rewriting an introspection response, and
   * clamping `load_tools` could make the escape hatch itself fail under budget
   * pressure. This records and nothing else.
   *
   * `batch` is skipped: `registerSessionTools` already records each dispatched
   * sub-call individually, so scoring the envelope too would count the same
   * response tokens twice. The sub-calls are the meaningful unit.
   */
  function recordMetaToolCalls(name: string, oArgs: unknown[]): void {
    if (name === 'batch') return;
    const cbIdx = oArgs.length - 1;
    const originalCb = oArgs[cbIdx];
    if (typeof originalCb !== 'function') return;
    const cb = originalCb as (...args: unknown[]) => unknown;
    oArgs[cbIdx] = async (...cbArgs: unknown[]) => {
      savings.recordCall(name);
      let result: unknown;
      try {
        result = await cb(...cbArgs);
      } catch (err) {
        savings.recordFailedCall(name);
        throw err;
      }
      const resultObj = result as WrappedToolResponse;
      const tokens = responseTokens(resultObj);
      if (tokens !== undefined) {
        if (resultObj?.isError) savings.recordFailedCall(name, tokens);
        else savings.recordActualTokens(name, tokens);
      }
      return result;
    };
  }

  // Wrap _originalTool so tools registered outside the gate (session meta-tools)
  // also get annotations injected automatically — and the always-load _meta
  // stamp, so meta-tools like `batch` (registered through this path) inherit
  // the same eager-load behaviour as gated tools.
  const annotatedOriginalTool = ((...oArgs: unknown[]) => {
    const oName = oArgs[0] as string;
    ungatedToolNames.push(oName);
    injectAnnotations(oArgs);
    recordMetaToolCalls(oName, oArgs);
    const registered = (_originalTool as (...args: unknown[]) => unknown)(...oArgs);
    stampAlwaysLoad(oName, registered);
    return registered;
  }) as typeof _originalTool;

  return {
    _originalTool: annotatedOriginalTool,
    registeredToolNames,
    ungatedToolNames,
    toolHandlers,
    deferredTools,
    isSessionLocalTool,
  };
}
